// Admin console contracts (doc 30). RBAC roles, append-only audit events, and
// admin DTOs shared by service-api (admin REST) and admin-web.

import type {
  BillingMode,
  Entitlement,
  PartnerCustomModelCredentialInput,
  PartnerCustomModelDefinition,
  PartnerCustomModelPricingInput,
  PartnerCustomModelProtocol,
  PartnerCustomModelRouteBackend,
  PlatformProviderKeyProvider,
  ProviderKeyTestErrorType,
  ServiceRole,
  ServiceUser,
  ThinkingLevel,
  VelugaModelCapabilities,
  VelugaModelCatalogItem,
} from './service.js';
import type { Group } from './service.js';
import type { UsageCacheDetails, UsageEvent, UsageKind } from './quota.js';

export type AdminEventType =
  // Retained for audit history and the immutable assistant migration after rollback.
  | 'assistant.turn.started'
  | 'assistant.turn.completed'
  | 'assistant.tool.called'
  | 'assistant.output.recorded'
  | 'assistant.denied'
  | 'assistant.plan.approved'
  | 'assistant.step.committed'
  | 'assistant.step.failed'
  | 'assistant.cancelled'
  | 'assistant.settings.updated'
  | 'auth.session.issue'
  | 'auth.session.rotate'
  | 'auth.session.reuse'
  | 'auth.session.revoke'
  | 'auth.login.password.succeeded'
  | 'auth.login.password.failed'
  | 'auth.password.changed'
  | 'auth.password.reset_requested'
  | 'auth.password.reset_skipped'
  | 'auth.password.reset_code.issued'
  | 'auth.password.reset_code.resent'
  | 'auth.password.reset_code.delivery_failed'
  | 'auth.password.reset_code.verified'
  | 'auth.password.reset_code.locked'
  | 'auth.password.reset_completed'
  | 'auth.password.reset_completed.delivery_failed'
  | 'auth.password.kept'
  | 'auth.signup.completed'
  | 'auth.signup.duplicate_notified'
  | 'auth.signup.failed'
  | 'auth.organization_sso.started'
  | 'auth.organization_sso.succeeded'
  | 'auth.organization_sso.denied'
  | 'auth.organization_sso.failed'
  | 'auth.organization_sso.replayed'
  | 'admin.organization_sso.created'
  | 'admin.organization_sso.updated'
  | 'admin.organization_sso.tested'
  | 'admin.organization_sso.enabled'
  | 'admin.organization_sso.disabled'
  | 'admin.organization_sso.deleted'
  | 'admin.user.invited'
  | 'admin.user.created'
  | 'admin.user.disabled'
  | 'admin.user.enabled'
  | 'admin.user.deleted'
  | 'admin.user.password.set'
  | 'admin.user.updated'
  | 'platform.admin.granted'
  | 'platform.admin.revoked'
  | 'admin.group.updated'
  | 'admin.seat.updated'
  | 'admin.model_allowlist.updated'
  | 'admin.tenant_login_default_model.updated'
  | 'admin.model_display.updated'
  | 'admin.model_route.updated'
  | 'admin.model_route.removed'
  | 'admin.gateway_backend.updated'
  | 'admin.routing_policy.updated'
  | 'admin.quota.updated'
  | 'admin.member_cap.updated'
  | 'admin.policy.updated'
  | 'admin.download_channel.updated'
  | 'platform.provider_key.registered'
  | 'platform.provider_key.preset_synced'
  | 'platform.provider_key.removed'
  | 'platform.provider_key.tested'
  | 'platform.provider_model.created'
  | 'platform.provider_model.updated'
  | 'platform.provider_model.disabled'
  | 'platform.provider_model.tested'
  | 'platform.provider_model.deployed'
  | 'platform.provider_model.bulk_deployed'
  | 'platform.provider_model.revoked'
  | 'platform.provider_model.rate_published'
  | 'platform.litellm.tested'
  | 'platform.web_search_key.updated'
  | 'platform.web_search_backend.updated'
  | 'platform.media_route.updated'
  | 'platform.model_assignment.updated'
  | 'platform.auto_routing.created'
  | 'platform.realtime_voice.settings_updated'
  | 'platform.recommended_models.updated'
  | 'platform.auto_routing.updated'
  | 'platform.auto_routing.enabled'
  | 'platform.auto_routing.disabled'
  | 'platform.auto_routing.created'
  | 'platform.auto_routing.updated'
  | 'platform.auto_routing.enabled'
  | 'platform.auto_routing.disabled'
  | 'platform.credit_rates.synced'
  | 'platform.credit_rates.coverage_warning'
  | 'platform.credit_rates.settings_updated'
  | 'platform.credit_rates.settings_published'
  | 'platform.credit_rates.settings_adopted'
  | 'platform.billing.settings_updated'
  | 'platform.billing.price_updated'
  | 'platform.billing.promotion_created'
  | 'platform.billing.promotion_updated'
  | 'platform.billing.promotion_deleted'
  | 'platform.billing.refund_completed'
  | 'platform.act_as_tenant'
  | 'platform.tenant.created'
  | 'platform.tenant.updated'
  | 'tenant.generative_ui.updated'
  | 'platform.tenant.deleted'
  | 'platform.tenant.self_signup.updated'
  | 'platform.partner.created'
  | 'platform.partner.updated'
  | 'platform.partner.deleted'
  | 'platform.partner.admin.granted'
  | 'platform.partner.admin.revoked'
  | 'platform.partner.model_scope.updated'
  | 'hwp_extension.download_granted'
  | 'hwp_extension.download_denied'
  | 'extension.download_granted'
  | 'extension.download_denied'
  | 'platform.partner.custom_model.updated'
  | 'platform.partner.custom_model.deleted'
  | 'platform.partner.custom_model.provider_tested'
  | 'platform.partner.custom_model.llm_tested'
  | 'platform.partner.custom_model.deployments_synced'
  | 'platform.custom_model.deployed'
  | 'platform.custom_model.revoked'
  | 'platform.partner.mcp_connector_scope.updated'
  | 'partner.tenant.created'
  | 'partner.tenant.updated'
  | 'partner.tenant.self_signup.updated'
  | 'partner.act_as_tenant'
  | 'partner.model_assignment.updated'
  | 'partner.custom_model.deployed'
  | 'partner.custom_model.revoked'
  | 'partner.tenant.mcp_connectors.updated'
  | 'credit.allocated'
  | 'platform.capability_source.updated'
  | 'platform.capability_source.assigned'
  | 'platform.capability_source.revoked'
  | 'platform.partner.capability_source_scope.updated'
  | 'partner.capability_source.assigned'
  | 'partner.capability_source.revoked'
  | 'tenant.capability_entry.toggled'
  | 'admin.control.rate_limited'
  | 'platform.extension.promoted'
  | 'platform.extension.rolled_back'
  | 'platform.extension.withdrawn'
  | 'admin.credit_limit.created'
  | 'admin.credit_limit.updated'
  | 'admin.credit_limit.deleted'
  | 'admin.quota_profile.created'
  | 'admin.quota_profile.updated'
  | 'admin.quota_profile.deactivated'
  | 'admin.user_quota_profile.pinned'
  | 'admin.user_quota_profile.unpinned'
  // Managed announcements. TWELVE values, not four: each authoring surface gets
  // its own namespace, matching the existing surface-prefix convention
  // (platform.partner.created / partner.tenant.created). Keep in lockstep with
  // the audit_events_event_type_check allowlist migration.
  | 'admin.announcement.created'
  | 'admin.announcement.updated'
  | 'admin.announcement.published'
  | 'admin.announcement.archived'
  | 'partner.announcement.created'
  | 'partner.announcement.updated'
  | 'partner.announcement.published'
  | 'partner.announcement.archived'
  | 'platform.announcement.created'
  | 'platform.announcement.updated'
  | 'platform.announcement.published'
  | 'platform.announcement.archived'
  | 'diagnostic_bundle.download';

export type AdminTargetType =
  // Retained targets describe historical rows; they grant no assistant permissions.
  | 'assistant_conversation'
  | 'assistant_plan'
  | 'assistant_settings'
  | 'realtime_voice_settings'
  | 'auth_session'
  | 'user'
  | 'group'
  | 'seat'
  | 'quota'
  | 'member_quota_default'
  | 'policy'
  | 'model_allowlist'
  | 'tenant_login_default_model'
  | 'model_display'
  | 'model_route'
  | 'gateway_backend'
  | 'routing_policy'
  | 'auto_routing_profile'
  | 'recommended_model_profile'
  | 'auto_routing_profile'
  | 'download_channel'
  | 'provider_key'
  | 'platform_provider_model'
  | 'platform_provider_model_deployment'
  | 'platform_web_search_key'
  | 'web_search_backend'
  | 'media_route'
  | 'model_assignment'
  | 'credit_rate_version'
  | 'billing_settings'
  | 'credit_product'
  | 'credit_promotion'
  | 'billing_order'
  | 'tenant'
  | 'partner'
  | 'partner_model_scope'
  | 'partner_custom_model'
  | 'hwp_extension'
  | 'extension'
  | 'partner_mcp_connector_scope'
  | 'credit_allocation'
  | 'capability_source'
  | 'partner_capability_source_scope'
  | 'tenant_capability_entry_toggle'
  | 'organization_sso_connection'
  | 'credit_limit'
  | 'quota_profile'
  | 'quota_profile_limit'
  | 'membership_quota_assignment'
  // One target type for all three announcement surfaces — the surface is already
  // carried by the event namespace.
  | 'announcement'
  | 'diagnostic_bundle';

export interface AdminAuditEvent {
  id: string;
  tenantId: string;
  actorUserId: string;
  /** When the write was performed through a platform act-as (impersonation)
   *  session, the id of the platform admin behind the session. Lets an auditor
   *  tell an impersonated action apart from a genuine tenant-member action and
   *  correlate it to the `platform.act_as_tenant` issuance row. Absent for
   *  ordinary (non-impersonated) writes. */
  actAsByUserId?: string;
  eventType: AdminEventType;
  targetType: AdminTargetType;
  targetId: string;
  beforeHash?: string;
  afterHash: string;
  createdAt: string;
}
// ── List pagination (opt-in) ─────────────────────────────────────────────
// Admin list endpoints accept these as query params. When NO param is sent
// the endpoint keeps returning the legacy unbounded array; when any param is
// present the response is a `Paginated<T>` envelope. This keeps every
// existing caller (desktop in-process client, older admin-web builds) intact
// while letting the console page/search large tenants.

/** Common opt-in list query: `?limit=&offset=&q=`. */
export interface ListQuery {
  /** Page size, 1–200. Defaults to 50 in envelope mode. */
  limit?: number;
  /** 0-based offset into the (filtered) result set. Defaults to 0. */
  offset?: number;
  /** Case-insensitive substring match; matched fields are endpoint-specific. */
  q?: string;
}

export interface DiagnosticBundleListQuery extends ListQuery {
  tenantId?: string;
  partnerId?: string;
  userId?: string;
}

export interface DiagnosticBundleView {
  id: string;
  tenantId: string;
  tenantName: string;
  partnerId?: string;
  userId: string;
  userEmail: string;
  userName: string;
  deviceId: string;
  appVersion: string;
  platform: 'win32' | 'darwin' | 'linux';
  sizeBytes: number;
  logFileCount: number;
  collectedAt: string;
  uploadedAt: string;
}

export interface UserListQuery extends ListQuery {
  groupId?: string;
}

/** Audit list query — adds an exact `eventType` filter on top of `q`. */
export interface AuditListQuery extends ListQuery {
  eventType?: string;
}

/** Envelope returned by list endpoints when a query param is present.
 *  `total` counts the filtered set BEFORE the limit/offset slice. */
export interface Paginated<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * `Group` enriched with the current member-user-id list. Returned by the
 * admin Groups list endpoint so the admin UI doesn't have to round-trip the
 * memberships table for every group.
 */
export interface AdminGroupView extends Group {
  memberIds: string[];
}

/**
 * Operational overview returned by `GET /v1/admin/overview`. Numbers only —
 * the admin UI is not a marketing dashboard.
 */
export interface AdminOverview {
  users: number;
  groups: number;
  catalogModels: number;
  gatewayBackends: number;
  credit?: {
    granted?: number;
    consumed: number;
    remaining?: number;
    unlimited: boolean;
  };
  partnerId?: string;
  partnerName?: string;
}

/** Admin-only user row enriched with tenant entitlement state. */
export interface AdminUserView extends ServiceUser {
  /** Tenant membership roles for the tenant currently being administered. */
  roles: ServiceRole[];
  seatStatus: Entitlement['seatStatus'];
  desktopAccess: Entitlement['desktopAccess'];
  downloadChannels: Entitlement['downloadChannels'];
  allowedPlatforms: Entitlement['allowedPlatforms'];
  minimumDesktopVersion?: Entitlement['minimumDesktopVersion'];
  /** True when this user holds platform-admin status (tracked in the global
   *  `platform_admins` registry, NOT a tenant membership role). Lets the admin
   *  console reflect/toggle Platform Admin state without a per-user round-trip. */
  isPlatformAdmin: boolean;
  /** True when this user is a partner admin for any partner (tracked in the
   *  global `partner_admins` registry, NOT a tenant membership role). Lets the
   *  user list surface partner-admin standing alongside the other roles. */
  isPartnerAdmin: boolean;
}

/** A partner admin resolved to its identifying user fields, so the partners
 *  console can list admins by email/name instead of an opaque user id. */
export interface PartnerAdminView {
  id: string;
  email: string;
  name: string;
}

/** Preview/result of moving a tenant between direct platform ownership and a
 * partner. The same shape is returned by dry-runs and committed transfers so
 * the console can render one confirmation summary. */
export interface TenantPartnerTransferResult {
  tenantId: string;
  fromPartnerId: string | null;
  toPartnerId: string | null;
  credit: {
    allocationRekeyed: boolean;
    creditMillis?: number;
    poolAfter?: {
      granted: number;
      committed: number;
      remaining: number;
    };
  };
  modelScopeAdded: string[];
  connectorScopeAdded: string[];
  capabilitySourceScopeAdded: string[];
  customModelsUndeployed: string[];
  consumedMovedMillis: number;
}

export interface PartnerOverview {
  partnerId: string;
  name: string;
  tenantCount: number;
  credit: { granted?: number; consumed: number; remaining?: number; unlimited: boolean };
  /** Lifetime credit per owned tenant, same millicredit unit and snapshot
   *  semantics as `credit`. One row per tenant `actor.partnerId` owns. */
  tenantCredits: Array<{
    tenantId: string;
    name: string;
    credit: { granted?: number; consumed: number; remaining?: number; unlimited: boolean };
  }>;
  modelScope: string[];
  usage: { from: string; to: string; requests: number; webSearchRequests: number; imageInputCount: number };
}

export interface PlatformAdminView {
  id: string;
  email: string;
  name: string;
}

/** Result of inviting a new admin user. The invite token is shown ONCE. */
export interface InvitedUser {
  user: ServiceUser;
  inviteToken: string;
  expiresAt: string;
}

/** Admin read projection; never persisted or included in usage hashes. */
export interface UsageEventView extends UsageEvent {
  /** Current email; null when the user or tenant membership no longer exists. */
  userEmail?: string | null;
}

export interface UsageQuery {
  tenantId?: string;
  from?: string;
  to?: string;
  userId?: string;
  groupId?: string;
  modelId?: string;
  conversationId?: string;
  usageKind?: UsageKind;
  hasImageInput?: boolean;
  limit?: number;
  afterSeq?: number;
}

export interface UsageTotalsView {
  realtimeVoiceSessions?: number;
  realtimeVoiceSeconds?: number;
  realtimeVoiceCreditMillis?: number;
  realtimeVoiceAudioInputTokens?: number;
  realtimeVoiceAudioOutputTokens?: number;
  realtimeVoiceTextInputTokens?: number;
  realtimeVoiceTextOutputTokens?: number;
  /** One-shot speech requests; never folded into chat token totals. */
  speechToTextRequests?: number;
  speechToTextCreditMillis?: number;
  textToSpeechRequests?: number;
  textToSpeechCreditMillis?: number;

  requests: number;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteInputTokens: number;
  /** Input units charged at the provider's cache-hit rate. */
  cachedUnits: number;
  /** Input units charged at the provider's cache-write rate. */
  cacheWriteUnits: number;
  /** Input units charged at the ordinary input rate. */
  nonCachedUnits: number;
  cacheKnownRequests: number;
  cacheUnknownRequests: number;
  /** Cached input divided by input tokens for requests with known cache data. */
  cacheTokenRatio: number;
  outputTokens: number;
  totalTokens: number;
  costMicroUsd: number;
  /** LLM-only headline credit retained for backwards compatibility. */
  creditMillis: number;
  /** Display credit across every usage kind. Unpriced events contribute zero. */
  totalCreditMillis: number;
  webSearchRequests: number;
  imageInputRequests: number;
  imageInputCount: number;
  imageInputBytes: number;
  imageGenerationCount: number;
  imageGenerationCreditMillis: number;
  videoGenerationSeconds: number;
  videoGenerationCreditMillis: number;
  pricedEvents: number;
  missingPriceEvents: number;
}

export interface UsageSummaryView extends UsageTotalsView {
  tenantId: string;
  from?: string;
  to?: string;
  hasCredit: boolean;
}

export interface CreditSettlementQuery {
  /** Required for a platform-scoped actor; tenant actors may omit it. */
  tenantId?: string;
  /** Number of UTC calendar months returned, including the current month. */
  months?: number;
  /** Operator-supplied contract horizon; allocation rows do not store an end date. */
  remainingMonths?: number;
}

export interface CreditSettlementUserRow {
  month: string;
  userId: string;
  requests: number;
  creditMillis: number;
}

export interface CreditSettlementMonthRow {
  month: string;
  activeUsers: number;
  creditMillis: number;
  users: CreditSettlementUserRow[];
}

export interface FreeTierSettlementRow {
  month: string;
  userId: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costMicroUsd: number;
  waivedCreditMicros: number;
}

export interface CreditSettlementReport {
  tenantId: string;
  generatedAt: string;
  timeZone: 'UTC';
  from: string;
  to: string;
  months: CreditSettlementMonthRow[];
  currentMonth: {
    month: string;
    usedCreditMillis: number;
    allocatedCreditMillis?: number;
    percentUsed?: number;
    resetsAt: string;
  };
  contract: {
    usedCreditMillis: number;
    totalBudgetCreditMillis?: number;
    remainingCreditMillis?: number;
    percentUsed?: number;
    remainingMonths: number;
    monthlyPaceCreditMillis?: number;
  };
  freeTier: FreeTierSettlementRow[];
}

export interface PartnerWebSearchUsageSummary {
  partnerId: string;
  tenantCount: number;
  from?: string;
  to?: string;
  webSearchRequests: number;
}

export interface UsageSeriesQuery extends UsageQuery {
  granularity?: 'hour' | 'day' | 'month';
  tz?: string;
}

export interface UsageSeriesPoint extends UsageTotalsView {
  bucket: string;
}

export interface UsageExportRow extends UsageTotalsView {
  tenantId: string;
  catalogModelId: string | null;
  mediaRouteId: string | null;
  mediaModelId: string | null;
  generationVariantKey: string | null;
  creditRateVersion: string | null;
  usageKind: UsageKind;
  cacheStatus: UsageCacheDetails['cacheStatus'] | null;
  // 'partial_rate': a credit snapshot exists but is missing the llm_tokens
  // component (the model has no rate entry), so the LLM tokens were billed at
  // zero while other components (e.g. image_input) were priced — under-billed.
  creditSource: 'priced' | 'missing_rate' | 'byo_unbilled' | 'missing_service_rate' | 'partial_rate';
}

export interface UsageChainViolation {
  seq?: number;
  requestId: string;
  reason: 'event_hash_mismatch' | 'prev_hash_mismatch';
}

/** Result of re-walking a tenant's usage hash chain (verifyUsageChain). */
export interface UsageChainVerification {
  valid: boolean;
  checkedEvents: number;
  violations: UsageChainViolation[];
}

export type ServiceUsageRateKind = 'web_search' | 'image_input' | 'image_output' | 'video_output';

export interface CreditRateRowView {
  version: string;
  catalogModelId: string;
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
  cachedInputUsdPerMillion?: number;
  cacheWriteInputUsdPerMillion?: number;
  marginMultiplier: number;
  effectiveFrom: string;
  promptTiers?: Array<{
    minimumInputTokens: number;
    inputUsdPerMillion: number;
    outputUsdPerMillion: number;
  }>;
}

export interface ServiceUsageRateRowView {
  version: string;
  kind: ServiceUsageRateKind;
  rateKey: string;
  unitName: 'request' | 'image' | 'second';
  costMicroUsdPerUnit: number;
  marginMultiplier: number;
  effectiveFrom: string;
}

export interface CreditRateServiceSetting {
  kind: ServiceUsageRateKind;
  rateKey: string;
  unitName: 'request' | 'image' | 'second';
  costMicroUsdPerUnit: number;
  marginMultiplier: number;
  provisional: boolean;
  sourceUrl: string;
  verifiedAt: string;
  note?: string;
}

export interface CreditRateUsdSetting {
  value: number;
  sourceUrl: string;
  verifiedAt: string;
  note?: string;
}

export type CreditRateServiceSettingInput = Omit<
  CreditRateServiceSetting,
  'marginMultiplier' | 'provisional'
> & {
  marginMultiplier?: number;
  provisional?: boolean;
};

export interface CreditRateSettingsView {
  voiceRates?: import('./realtime.js').RealtimeVoiceRate[];
  speechRates?: import('./realtime.js').SpeechRate[];
  serviceRates: CreditRateServiceSetting[];
  usdPerCredit?: CreditRateUsdSetting;
  drift: {
    serviceRates: Array<{ kind: ServiceUsageRateKind; rateKey: string }>;
    usdPerCredit: boolean;
  };
  publishedVersion?: string;
  publishedEffectiveFrom?: string;
}

export interface PutCreditRateSettingsRequest {
  voiceRates?: import('./realtime.js').RealtimeVoiceRateInput[];
  /** Replaces only the listed speech models; other published speech rates carry forward. */
  speechRates?: import('./realtime.js').SpeechRateInput[];
  expectedPublishedVersion: string;
  serviceRates?: CreditRateServiceSettingInput[];
  usdPerCredit?: CreditRateUsdSetting;
  removeServiceRates?: Array<{ kind: ServiceUsageRateKind; rateKey: string }>;
}

export interface PutCreditRateSettingsResult extends CreditRateSettingsView {
  warnings: Array<'usd_per_credit_changed' | 'provisional_rate_published'>;
}

export interface CreditRateTableView {
  voiceRates?: import('./realtime.js').RealtimeVoiceRate[];
  speechRates?: import('./realtime.js').SpeechRate[];
  version: string;
  usdPerCredit: number;
  effectiveFrom: string;
  rates: CreditRateRowView[];
  serviceRates: ServiceUsageRateRowView[];
}

export type PlatformProviderModelExecutionMode = 'chat-completions' | 'responses';

export interface PlatformProviderModelCapabilitiesInput {
  reasoning: boolean;
  vision: boolean;
  documentInput: boolean;
  toolUse: boolean;
  streaming: boolean;
  structuredOutput: boolean;
  webSearchNative?: boolean;
}

export interface PlatformProviderModelPricingEvidence {
  sourceUrl: string;
  verifiedAt: string;
  note?: string;
}

export interface PlatformProviderModelDefinition {
  provider: PlatformProviderKeyProvider;
  modelKey: string;
  displayName: string;
  description?: string;
  upstreamModelId: string;
  backendModelName: string;
  executionMode: PlatformProviderModelExecutionMode;
  capabilities: VelugaModelCapabilities;
  thinkingLevels?: ThinkingLevel[];
  limits: VelugaModelCatalogItem['limits'];
  pricingEvidence: PlatformProviderModelPricingEvidence;
  routingSafety?: VelugaModelCatalogItem['routing']['safety'];
  defaultForProvider: boolean;
  status: 'draft' | 'active' | 'disabled';
  revision: number;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  /** Admin read projection only; persistence rows omit this lifecycle summary. */
  deployments?: PlatformProviderModelDeployment[];
}

export interface PlatformProviderModelDeployment {
  provider: PlatformProviderKeyProvider;
  modelKey: string;
  tenantId: string;
  catalogModelId: string;
  routeId: string;
  definitionRevision: number;
  rateVersion?: string;
  /** Current effective pricing for this tenant catalog model. Read projection only. */
  currentRate?: PlatformProviderModelCurrentRate;
  status: 'active' | 'revoked';
  deployedBy: string;
  deployedAt: string;
  revokedAt?: string;
}

export interface PlatformProviderModelCurrentRate {
  version: string;
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
  cachedInputUsdPerMillion?: number;
  cacheWriteInputUsdPerMillion?: number;
  marginMultiplier: number;
  effectiveFrom: string;
  promptTiers?: Array<{
    minimumInputTokens: number;
    inputUsdPerMillion: number;
    outputUsdPerMillion: number;
  }>;
}

/**
 * A partner custom-model deployment into one tenant.
 *
 * Mirrors `PlatformProviderModelDeployment`. `rateVersion` is optional for
 * both deployment kinds because a free model (0/0 pricing) publishes no rate
 * version at all.
 *
 * Before this record existed the only way to enumerate deployments was to
 * re-derive the ids — and those ids are not reversible: neither carries
 * `partnerId`, and `customRouteId` rewrites `-` to `_` so the model key cannot
 * be recovered from it.
 */
export interface PartnerCustomModelDeploymentRecord {
  partnerId: string;
  modelKey: string;
  tenantId: string;
  catalogModelId: string;
  routeId: string;
  /** The definition revision this deployment was built from, so PR-4 can tell
   *  a stale deployment from a current one. */
  definitionRevision: number;
  /** Absent for a free model. */
  rateVersion?: string;
  status: 'active' | 'revoked';
  deployedBy: string;
  deployedAt: string;
  revokedAt?: string;
}

export interface CreatePlatformProviderModelRequest {
  provider: PlatformProviderKeyProvider;
  modelKey: string;
  displayName: string;
  description?: string;
  upstreamModelId: string;
  executionMode: PlatformProviderModelExecutionMode;
  capabilities: PlatformProviderModelCapabilitiesInput;
  thinkingLevels?: ThinkingLevel[];
  limits: VelugaModelCatalogItem['limits'];
  pricingEvidence: PlatformProviderModelPricingEvidence;
  routingSafety?: VelugaModelCatalogItem['routing']['safety'];
  defaultForProvider?: boolean;
}

export type UpdatePlatformProviderModelRequest = Omit<
  CreatePlatformProviderModelRequest,
  'provider' | 'modelKey'
> & { expectedRevision: number };

/** Partner custom-model create payload. Written by hand rather than derived
 *  with `Omit` so that server-owned fields (`revision`, `partnerId`,
 *  timestamps, derived `billingMode`) can never appear as request fields — a
 *  forged `revision` would otherwise bypass compare-and-swap. The sibling
 *  `CreatePlatformProviderModelRequest` is hand-written for the same reason. */
export interface CreatePartnerCustomModelRequest {
  modelKey: string;
  displayName: string;
  description?: string;
  protocol: PartnerCustomModelProtocol;
  routeBackend: PartnerCustomModelRouteBackend;
  capabilities: VelugaModelCapabilities;
  thinkingLevels?: ThinkingLevel[];
  limits: VelugaModelCatalogItem['limits'];
  pricing?: PartnerCustomModelPricingInput;
  status?: PartnerCustomModelDefinition['status'];
  providerCredential?: PartnerCustomModelCredentialInput;
}

/**
 * The editable surface of a partner custom model.
 *
 * Every field is optional and an omitted field is *kept*, not cleared. The
 * platform sibling chose PUT-with-replace for exactly the opposite semantics
 * ("a field the caller omits is cleared, not inherited"). Partner models cannot
 * follow it: preserving a stored secret when `providerCredential` is omitted is
 * the central requirement of this contract, and omission-means-clear collides
 * with that head-on. The accepted cost is that PATCH cannot blank a field.
 *
 * `capabilities`, `thinkingLevels`, `limits`, and the LiteLLM model name are
 * only editable while the model has no active deployment — they are copied onto
 * every deployed tenant catalog item, which desktop clients cache.
 */
export interface PartnerCustomModelPatch {
  displayName?: string;
  description?: string;
  /** Only the LiteLLM model name is patchable. `backendId` is derived from
   *  `(partnerId, modelKey)` and `protocol` is a fixed literal. */
  routeBackend?: { litellmModelName?: string };
  capabilities?: VelugaModelCapabilities;
  thinkingLevels?: ThinkingLevel[];
  limits?: VelugaModelCatalogItem['limits'];
  pricing?: PartnerCustomModelPricingInput;
  status?: PartnerCustomModelDefinition['status'];
  /** A complete object rotates the stored secret in place; a partial one is
   *  rejected. Omitting the field keeps the current credential. */
  providerCredential?: PartnerCustomModelCredentialInput;
}

/** Partner custom-model edit payload. `modelKey` is the URL identifier and is
 *  not editable — a rename is a delete plus a fresh create. */
export interface UpdatePartnerCustomModelRequest {
  expectedRevision: number;
  patch: PartnerCustomModelPatch;
}

/** Per-tenant outcome of the deployment resync an edit performs. */
export interface PartnerCustomModelDeploymentSyncResult {
  tenantId: string;
  catalogModelId: string;
  routeId: string;
  billingMode: BillingMode;
}

export interface UpdatePartnerCustomModelResult {
  model: PartnerCustomModelSummaryLike;
  /** Empty when the model has no active deployment. */
  deployments: PartnerCustomModelDeploymentSyncResult[];
  /** Present only when pricing actually changed on a paid model. */
  rateVersion?: string;
}

/** The redacted read shape, structurally. Declared here rather than importing
 *  `PartnerCustomModelSummary` from service-client, which would invert the
 *  dependency direction (service-client imports this file).
 *
 *  No `providerBaseUrl`: this shape backs `UpdatePartnerCustomModelResult.model`,
 *  and only the list read resolves that field. Declaring it here would advertise
 *  a value the write result never carries. */
export type PartnerCustomModelSummaryLike = Omit<
  PartnerCustomModelDefinition,
  'routeBackend'
> & { hasRouteBackend: boolean };

/** Which of the two diagnostics ran. Provider = reachability/auth against the
 *  customer's OpenAI-compatible endpoint, token-free. LLM = one minimal
 *  non-streaming completion through the Veluga LiteLLM transport, which does
 *  spend the partner's upstream tokens. */
export type PartnerCustomModelTestKind = 'provider' | 'llm';

/** `warning` means the check could not prove success but did not prove failure
 *  either — a server that does not implement `/models`, or a model the list
 *  does not mention. Registration is never blocked on a test result. */
export type PartnerCustomModelTestStatus = 'pass' | 'warning' | 'fail';

/**
 * Failure taxonomy. Deliberately NOT `ProviderKeyTestErrorType`, which collapses
 * every transport failure into one `network_error` bucket and has no dns/tls/
 * timeout members at all.
 *
 * `litellm_model_group_missing` is separate from `model_not_found` on purpose:
 * LiteLLM's `model_list` is hand-maintained YAML with no `/model/new` route, so
 * a name that matches no configured group and no provider wildcard fails
 * regardless of the credential. Reporting that as `model_not_found` sends the
 * operator to check the upstream model id when the real fix is an ops change to
 * config.yaml.
 */
export type PartnerCustomModelTestErrorType =
  | 'dns'
  | 'tls'
  | 'unreachable'
  | 'timeout'
  | 'auth'
  | 'not_found'
  | 'model_not_found'
  | 'litellm_model_group_missing'
  | 'no_credential'
  | 'backend_unavailable'
  | 'invalid_response'
  | 'upstream'
  | 'unknown';

/** Pre-save form values. The API key lives in memory for the duration of the
 *  request and is never persisted, logged, audited, or echoed back. */
export interface PartnerCustomModelDraftTestInput {
  protocol: PartnerCustomModelProtocol;
  litellmModelName: string;
  baseUrl: string;
  apiKey: string;
  litellmBaseUrl?: string;
}

/** Exactly one of the two forms. A body carrying both, or neither, is a 400. */
export type PartnerCustomModelTestRequest =
  { modelKey: string; draft?: never } | { draft: PartnerCustomModelDraftTestInput; modelKey?: never };

export interface PartnerCustomModelTestResult {
  kind: PartnerCustomModelTestKind;
  status: PartnerCustomModelTestStatus;
  latencyMs: number;
  checkedAt: string;
  httpStatus?: number;
  errorType?: PartnerCustomModelTestErrorType;
  /** Closed-set i18n key. Clients translate this and never render server
   *  message text, stack traces, or upstream error bodies. */
  messageCode: PartnerCustomModelTestMessageCode;
  /** Provider test only: whether the target model appeared in `/models`. This
   *  is a heuristic — the definition stores no `upstreamModelId`, so the name
   *  is derived by stripping a known wildcard prefix. */
  modelObserved?: boolean;
  /** LLM test only: control-stripped, length-capped assistant text. The one
   *  thing that distinguishes "200 OK" from "the model actually spoke". Never
   *  audited. */
  responsePreview?: string;
}

/**
 * The closed set of `messageCode` values, enumerated in one place so the
 * admin-web en/ko parity test can iterate it instead of the UI inventing
 * strings. Every `(status, errorType)` pair the service can produce maps to a
 * member of this array.
 */
export const PARTNER_CUSTOM_MODEL_TEST_MESSAGE_CODES = [
  // provider test
  'provider_ok',
  'provider_model_list_unsupported',
  'provider_model_not_observed',
  'provider_auth_failed',
  'provider_upstream_error',
  'provider_dns_failed',
  'provider_tls_failed',
  'provider_unreachable',
  'provider_timeout',
  'provider_unknown_error',
  // llm test
  'llm_ok',
  'llm_model_group_missing',
  'llm_auth_failed',
  'llm_model_not_found',
  'llm_invalid_response',
  'llm_upstream_error',
  'llm_dns_failed',
  'llm_tls_failed',
  'llm_unreachable',
  'llm_timeout',
  'llm_unknown_error',
  // resolution failures shared by both tests
  'no_credential',
  'backend_unavailable',
] as const;

export type PartnerCustomModelTestMessageCode =
  (typeof PARTNER_CUSTOM_MODEL_TEST_MESSAGE_CODES)[number];

export interface PlatformProviderModelListQuery extends ListQuery {
  provider?: PlatformProviderKeyProvider;
  status?: PlatformProviderModelDefinition['status'];
  /** Omit retired (disabled) definitions; ignored when `status` is set. */
  excludeDisabled?: boolean;
}

export interface PlatformProviderModelRateInput {
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
  cachedInputUsdPerMillion?: number;
  cacheWriteInputUsdPerMillion?: number;
  marginMultiplier?: number;
  promptTiers?: Array<{
    minimumInputTokens: number;
    inputUsdPerMillion: number;
    outputUsdPerMillion: number;
  }>;
  sourceUrl: string;
  verifiedAt: string;
  note?: string;
}

export interface TestPlatformProviderModelRequest {
  tenantId: string;
}

export interface PlatformProviderModelTestResult {
  provider: PlatformProviderKeyProvider;
  modelKey: string;
  tenantId: string;
  ok: boolean;
  latencyMs: number;
  status?: number;
  errorType?: ProviderKeyTestErrorType;
}

export interface DeployPlatformProviderModelRequest {
  tenantIds: string[];
  expectedRevision: number;
  rate: PlatformProviderModelRateInput;
}

export interface DeployPlatformProviderModelResult {
  definition: PlatformProviderModelDefinition;
  deployments: PlatformProviderModelDeployment[];
  rateVersion?: string;
}

export interface BulkDeployPlatformProviderModelsRequest {
  provider: PlatformProviderKeyProvider;
  modelKeys?: string[];
}

export interface BulkDeployPlatformProviderModelsResult {
  deployments: PlatformProviderModelDeployment[];
  rateVersion?: string;
  skipped: string[];
}

export interface PublishPlatformProviderModelRatesRequest {
  tenantIds: string[];
  expectedRevision: number;
  rate: PlatformProviderModelRateInput;
}

export interface PublishPlatformProviderModelRatesResult {
  rateVersion?: string;
  catalogModelIds: string[];
}

// Permission keys for the admin write/read surface.
export type AdminPermission =
  | 'admin.read'
  | 'admin.usage.read'
  | 'admin.users.write'
  | 'admin.groups.write'
  | 'admin.seats.write'
  | 'admin.quotas.write'
  | 'admin.allowlist.write'
  | 'admin.routes.write'
  | 'admin.backends.write'
  | 'admin.policies.write'
  | 'admin.downloads.write'
  | 'admin.audit.read'
  | 'admin.audit.export'
  | 'platform.read'
  | 'platform.act_as.write'
  | 'platform.provider_keys.write'
  | 'platform.provider_models.read'
  | 'platform.provider_models.write'
  | 'platform.model_assignment.write'
  | 'platform.auto_routing.manage'
  | 'platform.recommended_models.manage'
  | 'platform.credit_rates.read'
  | 'platform.credit_rates.write'
  | 'platform.billing.read'
  | 'platform.billing.write'
  | 'platform.billing.refund'
  | 'platform.tenants.write'
  | 'platform.admins.write'
  | 'platform.partners.write'
  | 'platform.extensions.read'
  | 'platform.extensions.write'
  | 'partner.read'
  | 'partner.admins.write'
  | 'partner.tenants.write'
  | 'partner.act_as.write'
  | 'partner.model_assignment.write'
  // Skill & agent-team distribution (PR5). Source registration is gated to
  // platform/partner; entry on/off toggling extends to tenant owner/admin.
  | 'platform.capability_sources.write'
  | 'platform.capability_sources.read'
  | 'partner.capability_sources.write'
  | 'partner.capability_sources.read'
  | 'admin.capability_sources.read'
  | 'admin.organization_sso.read'
  | 'admin.organization_sso.write'
  | 'admin.organization_sso.test'
  | 'admin.organization_sso.enable'
  // Managed announcements: one write key per authoring surface. Reading a
  // surface's announcements rides on that surface's existing read key
  // (admin.read / partner.read / platform.read). PLATFORM_ONLY_ROLES keeps the
  // three keys from ever landing on one actor.
  | 'admin.announcements.write'
  | 'partner.announcements.write'
  | 'platform.announcements.write'
  | 'capability_entries.toggle';

// Role → permission matrix (doc 30 §3). Service-side RBAC is authoritative;
// UI hiding is only a secondary measure.
export const ROLE_PERMISSIONS: Record<ServiceRole, AdminPermission[]> = {
  owner: [
    'admin.read',
    'admin.users.write',
    'admin.groups.write',
    'admin.seats.write',
    'admin.quotas.write',
    'admin.allowlist.write',
    'admin.routes.write',
    'admin.backends.write',
    'admin.policies.write',
    'admin.downloads.write',
    'admin.usage.read',
    'admin.audit.read',
    'admin.audit.export',
    'admin.capability_sources.read',
    'admin.organization_sso.read',
    'admin.organization_sso.write',
    'admin.organization_sso.test',
    'admin.announcements.write',
    'capability_entries.toggle',
  ],
  admin: [
    'admin.read',
    'admin.users.write',
    'admin.groups.write',
    'admin.seats.write',
    'admin.quotas.write',
    'admin.allowlist.write',
    'admin.routes.write',
    'admin.backends.write',
    'admin.policies.write',
    'admin.downloads.write',
    'admin.usage.read',
    'admin.audit.read',
    'admin.capability_sources.read',
    'admin.organization_sso.read',
    'admin.organization_sso.write',
    'admin.organization_sso.test',
    'admin.announcements.write',
    'capability_entries.toggle',
  ],
  billing_admin: ['admin.read', 'admin.usage.read', 'admin.audit.read'],
  auditor: [
    'admin.read',
    'admin.usage.read',
    'admin.audit.read',
    'admin.audit.export',
  ],
  member: [],
  platform_admin: [
    'admin.usage.read',
    'platform.read',
    'platform.act_as.write',
    'platform.provider_keys.write',
    'platform.provider_models.read',
    'platform.provider_models.write',
    'platform.model_assignment.write',
    'platform.auto_routing.manage',
    'platform.recommended_models.manage',
    'platform.credit_rates.read',
    'platform.credit_rates.write',
    'platform.billing.read',
    'platform.billing.write',
    'platform.billing.refund',
    'platform.tenants.write',
    'platform.admins.write',
    'platform.partners.write',
    'platform.extensions.read',
    'platform.extensions.write',
    'platform.capability_sources.write',
    'platform.capability_sources.read',
    'admin.organization_sso.read',
    'admin.organization_sso.write',
    'admin.organization_sso.test',
    'admin.organization_sso.enable',
    'platform.announcements.write',
    'capability_entries.toggle',
  ],
  // Partner admins act ACROSS their own tenants. Tenant-admin powers are NOT
  // granted directly here — a partner obtains them by act-as'ing INTO a tenant
  // it owns (yielding an `owner` token), exactly like a platform admin. The
  // partner.* permissions cover partner-scoped operations only.
  partner_admin: [
    'partner.read',
    'partner.admins.write',
    'partner.tenants.write',
    'partner.act_as.write',
    'partner.model_assignment.write',
    'admin.usage.read',
    'partner.capability_sources.write',
    'partner.capability_sources.read',
    'partner.announcements.write',
    'admin.organization_sso.read',
    'admin.organization_sso.write',
    'admin.organization_sso.test',
    'admin.organization_sso.enable',
    'capability_entries.toggle',
  ],
};

/** Roles that are PLATFORM/PARTNER-scoped and must never be granted on a tenant
 *  membership. `platform_admin`/`partner_admin` carry `platform.*`/`partner.*`
 *  permissions; if either ever landed in a membership, a tenant-scoped actor
 *  would resolve to those permissions and the tenant/platform/partner permission
 *  surfaces would no longer be disjoint. Their status is tracked exclusively by
 *  the `platformAdmins`/`partnerAdmins` registries, never by memberships. */
export const PLATFORM_ONLY_ROLES: readonly ServiceRole[] = [
  'platform_admin',
  'partner_admin',
];

/** True when `role` may be assigned to a tenant membership (i.e. it is not a
 *  platform-only role). Membership writes and act-as grants are clamped to this. */
export function isTenantAssignableRole(role: ServiceRole): boolean {
  return !PLATFORM_ONLY_ROLES.includes(role);
}

/** Tenant display names are restricted to ASCII letters, digits, spaces, and
 *  hyphens, and must start and end with a letter or digit. Shared by the
 *  admin-web create-tenant form (client-side guard) and service-api
 *  (authoritative validation) so the two rules never drift apart. */
export const TENANT_NAME_PATTERN =
  /^[A-Za-z0-9](?:[A-Za-z0-9 -]*[A-Za-z0-9])?$/;

/** URL/id-safe tenant slug: lowercase alphanumeric groups joined by single
 *  hyphens, with no leading, trailing, or doubled hyphens. */
export const TENANT_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** True when `name` is a valid tenant display name. Callers pass an already
 *  trimmed value — this does not trim. */
export function isValidTenantName(name: string): boolean {
  return TENANT_NAME_PATTERN.test(name);
}

/** True when `slug` is a valid tenant slug. Callers pass an already trimmed +
 *  lowercased value — this does not normalize. */
export function isValidTenantSlug(slug: string): boolean {
  return TENANT_SLUG_PATTERN.test(slug);
}
